import { useEffect, useState, useCallback } from 'react';
import API from '../api/api';
import { Toaster, toast } from 'react-hot-toast';
import { format } from 'date-fns';
import JobInfoForm from '../components/forms/JobInfoForm';
import WellInfoForm from '../components/forms/WellInfoForm';
import RunInformationForm from '../components/forms/RunInformationForm';
import TieOnInfoForm from '../components/forms/TieOnInfoForm';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from "framer-motion";

const defaultJobInfo = {
  id: null,
  job_number: '',
  customer_name: '',
  client_name: '',
  rig_number: '',
  well_name: '',
  well_id: '',
  well_location: '',
  client_rep: '',
  survey_method: 'Minimum Curvature',
  start_date: '',
};

const defaultWellInfo = {
  id: null,
  latitude_degrees: '',
  latitude_minutes: '',
  latitude_seconds: '',
  longitude_degrees: '',
  longitude_minutes: '',
  longitude_seconds: '',
  northing_value: '',
  easting_value: '',
  proposed_direction: '',
  ground_level_elevation: '',
  central_meridian: '',
  max_temp: '',
  well_type: '',
};

export const defaultRunInfo = {
  id: null,
  survey_from: '',
  survey_to: '',
  survey_meterage: '',
  survey_type: '',
  proposed_direction: '',
  elevation_referred_to: '',
  reference_datum: '',
  reference_height: '',
  geodetic_system: 'Universal Transverse Mercator',
  geodetic_datum: '',
  map_zone: '',
  max_depth: '',
  max_inclination: '',
  max_temp: '',
  north_reference: '',
  exp_inclination: '',
  hole_section: '',
  survey_run_in: '',
  minimum_id: '',
  job: null,
  well: null
};

const Project = () => {
  const [projects, setProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [wellId, setWellId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('Job Info');
  const [newProject, setNewProject] = useState(null);
  const [jobInfoFormData, setJobInfoFormData] = useState(defaultJobInfo);
  const [wellInfoFormData, setWellInfoFormData] = useState(defaultWellInfo);
  const [runInfoFormData, setRunInfoFormData] = useState(defaultRunInfo);
  const [activeRunId, setActiveRunId] = useState(null);
  const [activeTieOnId, setActiveTieOnId] = useState(null);
  const [isNewProject, setIsNewProject] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false); 
  const [projectType, setProjectType] = useState(null);
  const [country, setCountry] = useState('');
  const navigate = useNavigate();
  const [buttonPos, setButtonPos] = useState(null);

  const fetchProjects = useCallback(async () => {
    try {
      const res = await API.get('projects/');
      const sorted = res.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setProjects(sorted);
    } catch (err) {
      toast.error('Failed to fetch projects');
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const createProject = async () => {
    setLoading(true);
    try {
      const res = await API.post('projects/', { project_type: 'job' });
      toast.success('Project created');
      setProjects((prev) => [res.data, ...prev]);
      setNewProject(res.data);
      setShowModal(true);
      setActiveTab('Job Info');
      setIsNewProject(true);
      
      // Reset all form data
      setJobInfoFormData(defaultJobInfo);
      setWellInfoFormData(defaultWellInfo);
      setRunInfoFormData(defaultRunInfo);
      setWellId(null);
      setActiveRunId(null);
      setActiveTieOnId(null);
    } catch (err) {
      toast.error('Failed to create project');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (project) => {
    // Reset all form data before loading new project
    setJobInfoFormData(defaultJobInfo);
    setWellInfoFormData(defaultWellInfo);
    setRunInfoFormData(defaultRunInfo);
    setWellId(null);
    setActiveRunId(null);
    setActiveTieOnId(null);
    
    setNewProject(project);
    setShowModal(true);
    setActiveTab('Job Info');
    setIsNewProject(false);

    try {
      // Fetch job info
      const jobRes = await API.get(`jobs/?project=${project.id}`);
      if (jobRes.data?.length > 0) {
        const job = jobRes.data[0];
        setJobInfoFormData(job);
        
        // Fetch well info
        try {
          const wellRes = await API.get(`wellinfo/by-job/${job.id}/`);
          setWellInfoFormData(wellRes.data);
          setWellId(wellRes.data.id);

          // Initialize run info with job and well references
          setRunInfoFormData({
            ...defaultRunInfo,
            job: job.id,
            well: wellRes.data.id
          });

          // Fetch run info to get tie on data
          try {
            const runRes = await API.get(`/runs/?well=${wellRes.data.id}&job=${job.id}`);
            if (runRes.data && runRes.data.length > 0) {
              const run = runRes.data[0];
              setActiveRunId(run.id);
              
              // Check for tie on info
              if (run.tie_on) {
                setActiveTieOnId(run.tie_on.id);
              }
            }
          } catch (runErr) {
            console.error("Error fetching run info:", runErr);
          }
        } catch (wellErr) {
          if (wellErr.response?.status === 404) {
            setWellInfoFormData(defaultWellInfo);
            setWellId(null);
          } else {
            throw wellErr;
          }
        }
      } else {
        // If no job exists, get the next job number for this project
        try {
          const params = { project_type: project.project_type, country: project.country || "" };
          const nextJobRes = await API.get("jobs/next-number/", { params });
          setJobInfoFormData({
            ...defaultJobInfo,
            job_number: nextJobRes.data.next_job_number
          });
        } catch (err) {
          console.error("Failed to fetch next job number:", err);
          setJobInfoFormData(defaultJobInfo);
        }
      }
    } catch (err) {
      console.warn("Error fetching project data", err);
      setJobInfoFormData(defaultJobInfo);
      setWellInfoFormData(defaultWellInfo);
      setWellId(null);
    }
  };

  const filteredProjects = projects
    .filter((p) =>
      p.project_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.project_type?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return (
    <>
      <div className={`min-h-screen  bg-gray-100 dark:bg-gray-900 flex justify-center items-start py-8 px-4 mt-16 ${showModal ? 'filter blur-sm' : ''}`}>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              fontSize: '14px',
              borderRadius: '10px',
              background: '#333',
              color: '#fff',
            },
            success: {
              style: {
                background: 'green',
                color: 'white',
              },
            },
            error: {
              style: {
                background: '#C5172E',
                color: 'white',
              },
            },
            loading: {
              style: {
                background: 'blue',
                color: 'white',
              },
            },
          }}
        />
        <div className="w-full max-w-8xl bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">

          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Project Details</h2>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <input
                type="text"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full sm:w-64 px-3 py-2 text-sm border border-gray-300 rounded-md dark:bg-gray-800 dark:text-gray-100"
              />
              <button
                onClick={() => setShowTypeModal(true)}
                disabled={loading}
                className="bg-red-600 text-white px-4 py-2 text-sm rounded-md hover:bg-red-700 disabled:opacity-50 transition"
              >
                {loading ? 'Starting...' : 'Start Project'}
              </button>
            </div>
          </div>

          <hr className="border-gray-300 dark:border-gray-600 mb-4" />

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2">Project ID</th>
                  <th className="px-3 py-2">Project Type</th>
                  <th className="px-3 py-2">Created At</th>
                  <th className="px-3 py-2">Completed At</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.length > 0 ? (
                  filteredProjects.map((project) => (
                    <tr
                      key={project.id}
                      className="border-t dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <td className="px-3 py-2 text-gray-600 dark:text-gray-200">{project.project_id}</td>
                      <td className="px-3 py-2 capitalize text-gray-600 dark:text-gray-200">{project.project_type}</td>
                      <td className="px-3 py-2 text-gray-600 dark:text-gray-200">
                        {project.created_at ? format(new Date(project.created_at), 'dd-MM-yyyy') : '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-600 dark:text-gray-200">
                        {project.completed_at ? format(new Date(project.completed_at), 'yyyy-MM-dd') : '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-600 dark:text-gray-200">
                        {project.completed_at ? (
                          <span className="text-green-600 font-medium">Completed</span>
                        ) : (
                          <span className="text-yellow-600 font-medium">In Progress</span>
                        )}
                      </td>
                      <td
                        className="px-3 py-2 text-blue-600 hover:underline cursor-pointer"
                        onClick={() => handleView(project)}
                      >
                        View
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4 text-gray-400">
                      No matching projects.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      {/* First Modal - Select Job/Test */}
      <AnimatePresence>
        {showTypeModal && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Background Overlay */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-[2px]"
              onClick={() => setShowTypeModal(false)}
            />

            {/* Modal Box */}
            <motion.div
              initial={{
                scale: 0.2,
                opacity: 0,
                x: buttonPos ? buttonPos.x - window.innerWidth / 2 : 0,
                y: buttonPos ? buttonPos.y - window.innerHeight / 2 : 0,
              }}
              animate={{ scale: 1, opacity: 1, x: 0, y: 0 }}
              exit={{
                scale: 0.2,
                opacity: 0,
                x: buttonPos ? buttonPos.x - window.innerWidth / 2 : 0,
                y: buttonPos ? buttonPos.y - window.innerHeight / 2 : 0,
              }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 25,
              }}
              className="relative bg-white dark:bg-gray-800 w-full max-w-md rounded-xl shadow-lg p-6 z-60"
            >
              {/* Close Button */}
              <button
                onClick={() => setShowTypeModal(false)}
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
              >
                ✕
              </button>

              <h2 className="text-lg font-semibold mb-4 dark:text-gray-100">
                Select Project Type
              </h2>

              {/* Radio Buttons */}
              <div className="mb-4">
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="projectType"
                      value="job"
                      checked={projectType === "job"}
                      onChange={(e) => setProjectType(e.target.value)}
                    />
                    Job
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="projectType"
                      value="test"
                      checked={projectType === "test"}
                      onChange={(e) => setProjectType(e.target.value)}
                    />
                    Test
                  </label>
                </div>
              </div>

              {/* Country Dropdown if Job */}
              <AnimatePresence>
                {projectType === "job" && (
                  <motion.div
                    key="country-select"
                    initial={{ opacity: 0, height: 0, y: -10 }}
                    animate={{ opacity: 1, height: "auto", y: 0 }}
                    exit={{ opacity: 0, height: 0, y: -10 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="mb-4 overflow-hidden"
                  >
                    <label className="block font-medium text-gray-700 dark:text-gray-200 mb-2">
                      Select Country
                    </label>
                    <select
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-gray-100"
                    >
                      <option value="">-- Select Country --</option>
                      <option value="oman">Oman</option>
                      <option value="kuwait">Kuwait</option>
                      <option value="qatar">Qatar</option>
                      <option value="uk">UK</option>
                    </select>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Continue Button */}
              <button
                onClick={async () => {
                  if (!projectType) {
                    toast.error("Please select Job or Test");
                    return;
                  }
                  if (projectType === "job" && !country) {
                    toast.error("Please select a country for Job project");
                    return;
                  }

                  try {
                    setLoading(true);
                    const res = await API.post("projects/", {
                      project_type: projectType,
                      country: projectType === "job" ? country : null,
                    });
                    toast.success("Project created");
                    setProjects((prev) => [res.data, ...prev]);
                    setNewProject(res.data);

                    // Get the next job number for the new project
                    try {
                      const params = { project_type: projectType, country: country || "" };
                      const nextJobRes = await API.get("jobs/next-number/", { params });
                      setJobInfoFormData({
                        ...defaultJobInfo,
                        job_number: nextJobRes.data.next_job_number
                      });
                    } catch (err) {
                      console.error("Failed to fetch next job number:", err);
                    }

                    // open next modal
                    setShowTypeModal(false);
                    setShowModal(true);
                    setActiveTab("Job Info");
                    setIsNewProject(true);
                  } catch (err) {
                    toast.error("Failed to create project");
                  } finally {
                    setLoading(false);
                  }
                }}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition"
              >
                {loading ? "Creating..." : "Continue"}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Project Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowModal(false)} />
          <div className="relative bg-white dark:bg-gray-800 w-full max-w-5xl rounded-xl shadow-lg p-6 z-60 overflow-hidden h-[80vh]">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-3 right-3 text-gray-600 hover:text-black dark:hover:text-white text-2xl font-bold leading-none"
              aria-label="Close modal"
            >
              ×
            </button>

            <h2 className="text-lg font-semibold mb-4 dark:text-gray-100">
              {newProject?.project_id}
            </h2>

            {/* Tabs */}
            <div className="flex border-b mb-4 overflow-x-auto">
              {['Job Info', 'Asset Info', 'Well Info', 'Run Info', 'Tie-On Info'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 text-xs font-medium whitespace-nowrap ${
                    activeTab === tab
                      ? 'border-b-2 border-red-600 text-red-600'
                      : 'text-gray-500 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-500'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {activeTab === 'Job Info' && (
              <JobInfoForm
                projectId={newProject?.id}
                formData={jobInfoFormData}
                setFormData={setJobInfoFormData}
                setActiveTab={setActiveTab}
                projectType={newProject?.project_type}
                country={newProject?.country}
                isNewProject={isNewProject}
              />
            )}

            {activeTab === 'Well Info' && (
              <WellInfoForm
                jobId={jobInfoFormData.id}
                formData={wellInfoFormData}
                setFormData={setWellInfoFormData}
                setWellId={setWellId}
                setActiveTab={setActiveTab}
                onSuccess={(wellData) => {
                  setWellId(wellData.id);
                  // Update run info with the new well ID
                  setRunInfoFormData(prev => ({
                    ...prev,
                    well: wellData.id,
                    job: jobInfoFormData.id
                  }));
                }}
              />
            )}

            {activeTab === 'Run Info' && (
              <RunInformationForm
                jobId={jobInfoFormData.id}
                wellId={wellId}
                formData={runInfoFormData}
                setFormData={setRunInfoFormData}
                isNewProject={isNewProject}
                onSuccess={(runData) => {
                  setRunInfoFormData(runData);
                  setActiveRunId(runData.id);
                  toast.success('Run info saved successfully!');
                  setActiveTab('Tie-On Info');
                }}
              />
            )}

            {activeTab === 'Tie-On Info' && (
              <TieOnInfoForm
                runId={activeRunId}
                isNewProject={isNewProject}
                onSuccess={(tieOnData) => {
                  setActiveTieOnId(tieOnData.id);
                  setShowModal(false);
                  fetchProjects();
                  navigate(`/jobdetails/${jobInfoFormData.id}`);
                }}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Project;